import React, { useState, useEffect } from 'react';
import { loansAPI } from '../../services/api';
import { Loan, RecordLoanPaymentDto } from '../../types';
import { useTranslation } from 'react-i18next';
import {
  XMarkIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { toastSuccess, toastError } from '../../utils/toast';

interface LoanPaymentModalProps {
  loan: Loan;
  onClose: () => void;
  onSuccess: () => void;
}

const LoanPaymentModal: React.FC<LoanPaymentModalProps> = ({
  loan,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<RecordLoanPaymentDto>({
    loanId: loan.id,
    paymentAmount: loan.scheduledPaymentAmount || 0,
    principalAmount: undefined,
    interestAmount: undefined,
    lateFeeAmount: undefined,
    paymentDate: new Date().toISOString().split('T')[0], // Today's date
    paymentMethod: 'cash',
    notes: '',
    receiptNumber: '',
  });

  // Calculate monthly interest based on loan's interest rate
  const calculateInterestAmount = (): number => {
    if (!loan.currentBalance || !loan.monthlyInterestRate) return 0;
    return (loan.currentBalance * loan.monthlyInterestRate) / 100;
  };

  // Get calculated interest amount for display
  const getInterestAmount = (): number => {
    const calculatedInterest = calculateInterestAmount();
    return calculatedInterest > 0 ? Math.round(calculatedInterest * 100) / 100 : 0;
  };

  // Get calculated principal amount for display
  const getPrincipalAmount = (): number => {
    const scheduledAmount = loan.scheduledPaymentAmount || 0;
    const interestAmount = getInterestAmount();
    return Math.max(0, scheduledAmount - interestAmount);
  };

  // Initialize payment breakdown on component load
  useEffect(() => {
    const interestAmount = getInterestAmount();
    const principalAmount = getPrincipalAmount();
    
    setFormData(prev => ({
      ...prev,
      principalAmount: principalAmount,
      interestAmount: interestAmount,
      paymentAmount: principalAmount + interestAmount + (prev.lateFeeAmount || 0),
    }));
  }, []);

  // Calculate total payment amount from individual components
  const getTotalPaymentAmount = (): number => {
    return getPrincipalAmount() + getInterestAmount() + (formData.lateFeeAmount || 0);
  };

  // Format currency for display
  const formatCurrencyInput = (value: number | undefined): string => {
    if (value === undefined || value === null) return '';
    return value.toFixed(2);
  };

  // Parse currency input back to number
  const parseCurrencyInput = (value: string): number | undefined => {
    if (value === '' || value === null) return undefined;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? undefined : parsed;
  };

  // Update total payment amount when late fee changes
  useEffect(() => {
    const totalAmount = getTotalPaymentAmount();
    const interestAmount = getInterestAmount();
    const principalAmount = getPrincipalAmount();
    
    setFormData(prev => ({
      ...prev,
      principalAmount: principalAmount,
      interestAmount: interestAmount,
      paymentAmount: totalAmount,
    }));
  }, [formData.lateFeeAmount]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    let processedValue: any = value;
    
    // Handle currency inputs (only lateFeeAmount is editable)
    if (type === 'number' && name === 'lateFeeAmount') {
      processedValue = parseCurrencyInput(value);
    } else if (type === 'number') {
      processedValue = value === '' ? 0 : parseFloat(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
    }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    const totalPayment = getTotalPaymentAmount();
    
    if (totalPayment <= 0) {
      errors.paymentAmount = t('loans.payment.validation.amountRequired');
    }
    
    if (totalPayment > loan.currentBalance + (loan.totalLateFees || 0) + 1000) { // Add buffer for interest
      errors.paymentAmount = t('loans.payment.validation.amountTooHigh');
    }
    
    if ((formData.lateFeeAmount || 0) < 0) {
      errors.lateFeeAmount = t('loans.payment.validation.amountInvalid');
    }
    
    if (!formData.paymentDate) {
      errors.paymentDate = t('loans.payment.validation.dateRequired');
    }
    
    if (!formData.paymentMethod) {
      errors.paymentMethod = t('loans.payment.validation.methodRequired');
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const paymentData: RecordLoanPaymentDto = {
        ...formData,
        paymentDate: formData.paymentDate,
      };

      await loansAPI.recordPayment(paymentData);
      
      toastSuccess(t('loans.payment.paymentRecorded'));
      onSuccess();
    } catch (err: any) {
      console.error('Error recording payment:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(t('loans.payment.errorRecording'));
      }
      toastError(t('loans.payment.errorRecording'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {t('loans.payment.recordPayment')}
                </h3>
                <p className="text-sm text-gray-500">
                  {loan.borrower?.name} - {formatCurrency(loan.currentBalance)} {t('loans.payment.remaining')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-md text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-800">{error}</div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Payment Breakdown */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                {t('loans.payment.form.paymentBreakdown')}
              </h4>
              
              {/* Principal Amount - Read Only */}
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{t('loans.payment.form.principal')}:</span>
                <span className="text-sm font-semibold text-gray-900">
                  ${getPrincipalAmount().toFixed(2)}
                </span>
              </div>

              {/* Interest Amount - Read Only */}
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <span className="text-sm text-gray-600">{t('loans.payment.form.interest')}:</span>
                  <span className="text-xs text-gray-400 ml-1">
                    ({loan.monthlyInterestRate}% {t('loans.payment.form.monthlyRate')})
                  </span>
                </div>
                <span className="text-sm font-semibold text-blue-600">
                  ${getInterestAmount().toFixed(2)}
                </span>
              </div>

              {/* Calculation Details */}
              <div className="pt-2 border-t border-gray-200">
                <div className="text-xs text-gray-500">
                  <div className="flex justify-between">
                    <span>{t('loans.payment.form.currentBalance')}:</span>
                    <span>${(loan.currentBalance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('loans.payment.form.interestCalculation')}:</span>
                    <span>${(loan.currentBalance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} × {loan.monthlyInterestRate}%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Late Fees Amount */}
            <div>
              <label htmlFor="lateFeeAmount" className="block text-sm font-medium text-gray-700">
                {t('loans.payment.form.lateFees')}
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  id="lateFeeAmount"
                  name="lateFeeAmount"
                  step="0.01"
                  min="0"
                  value={formatCurrencyInput(formData.lateFeeAmount)}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                    fieldErrors.lateFeeAmount ? 'border-red-300' : ''
                  }`}
                  placeholder=""
                />
              </div>
              {fieldErrors.lateFeeAmount && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.lateFeeAmount}</p>
              )}
            </div>

            {/* Payment Total */}
            <div className="bg-gray-50 rounded-md p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-900">{t('loans.payment.form.paymentAmount')}:</span>
                <span className="text-lg font-semibold text-gray-900">
                  {formatCurrency(getTotalPaymentAmount())}
                </span>
              </div>
              {fieldErrors.paymentAmount && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.paymentAmount}</p>
              )}
            </div>

            {/* Payment Date */}
            <div>
              <label htmlFor="paymentDate" className="block text-sm font-medium text-gray-700">
                {t('loans.payment.form.paymentDate')} <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="paymentDate"
                name="paymentDate"
                value={formData.paymentDate}
                onChange={handleInputChange}
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                  fieldErrors.paymentDate ? 'border-red-300' : ''
                }`}
              />
              {fieldErrors.paymentDate && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.paymentDate}</p>
              )}
            </div>

            {/* Payment Method */}
            <div>
              <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700">
                {t('loans.payment.form.paymentMethod')} <span className="text-red-500">*</span>
              </label>
              <select
                id="paymentMethod"
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleInputChange}
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                  fieldErrors.paymentMethod ? 'border-red-300' : ''
                }`}
              >
                <option value="cash">{t('loans.payment.methods.cash')}</option>
                <option value="credit_card">{t('loans.payment.methods.creditCard')}</option>
                <option value="debit_card">{t('loans.payment.methods.debitCard')}</option>
                <option value="bank_transfer">{t('loans.payment.methods.bankTransfer')}</option>
                <option value="digital_wallet">{t('loans.payment.methods.digitalWallet')}</option>
              </select>
              {fieldErrors.paymentMethod && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.paymentMethod}</p>
              )}
            </div>

            {/* Receipt Number */}
            <div>
              <label htmlFor="receiptNumber" className="block text-sm font-medium text-gray-700">
                {t('loans.payment.form.receiptNumber')}
              </label>
              <input
                type="text"
                id="receiptNumber"
                name="receiptNumber"
                value={formData.receiptNumber}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder={t('loans.payment.form.receiptPlaceholder')}
              />
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                {t('loans.payment.form.notes')}
              </label>
              <textarea
                id="notes"
                name="notes"
                rows={3}
                value={formData.notes}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder={t('loans.payment.form.notesPlaceholder')}
              />
            </div>

            {/* Actions */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {t('actions.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading || getTotalPaymentAmount() <= 0}
                className="inline-flex items-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    {t('loans.payment.recording')}
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="mr-2 h-4 w-4" />
                    {t('loans.payment.recordPayment')}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoanPaymentModal;
